import { motion } from "motion/react";

import { Dispatch, JSX, SetStateAction, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Content } from "@/@types/general.ts";
import { ContentHistory } from "@/api/feed/feed.types.ts";
import { ROUTES } from "@/config/routes.ts";

interface MovieCardContentProps
  extends Omit<Content, "is_soon" | "is_serial">,
    Partial<Pick<ContentHistory, "time_code" | "duration">> {
  showPreview: () => void;
  hidePreview: () => void;
  setReferenceElement: Dispatch<SetStateAction<HTMLElement | null>>;
}

const MovieCardContent = forwardRef<HTMLImageElement, MovieCardContentProps>(
  (
    {
      time_code,
      duration,
      poster_h,
      original_name,
      is_new,
      is_free,
      is_russian,
      id,
      distributor,
      showPreview,
      hidePreview,
      setReferenceElement,
    },
    ref,
  ): JSX.Element => {
    const { t } = useTranslation();

    return (
      <Link
        onMouseOver={showPreview}
        onMouseOut={hidePreview}
        ref={setReferenceElement}
        to={ROUTES.DETAIL.MAIN.replace(":id", id?.toString())}
        title={original_name}
        className={"relative block rounded-[0.75rem] text-sm leading-4"}
      >
        <div className="overflow-hidden rounded-[0.75rem]">
          <motion.img
            ref={ref}
            key={"Poster"}
            src={poster_h?.small}
            alt={original_name}
            loading={"lazy"}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
            className={
              "aspect-video h-auto w-full rounded-[0.75rem] bg-surface-container object-cover object-center max-lg:rounded-[0.625rem]"
            }
          />
        </div>

        {is_new && !is_free ? (
          <span
            className={
              "absolute -left-[5px] top-[6px] rounded-xl bg-orange-1 px-2 py-1 text-sm/none font-medium text-on-primary   max-lg:p-0.5 max-lg:text-[0.625rem]/[0.75rem]"
            }
          >
            {t("new-tag")}
          </span>
        ) : null}
        {is_free ? (
          <span
            className={
              "absolute -left-[5px] top-[6px] rounded-xl bg-orange-1 px-2 py-1 text-sm/none font-medium text-on-primary   max-lg:p-0.5 max-lg:text-[0.625rem]/[0.75rem]"
            }
          >
            {t("free")}
          </span>
        ) : null}
        <div className={"absolute right-1 top-1 flex gap-px"}>
          {distributor?.icon_svg ? (
            <span
              className={
                "distributor-icon-light rounded-2xl bg-black bg-opacity-40 px-3 py-1 max-lg:px-0.5 max-lg:py-[0.1875rem]"
              }
            >
              <img src={distributor.icon_svg} alt={distributor.name} className={"h-4 w-auto max-lg:h-2.5"} />
            </span>
          ) : (
            <span
              className={
                "distributor-icon-light rounded-2xl bg-black bg-opacity-40 px-3 py-1 max-lg:px-2 max-lg:py-[0.1875rem]"
              }
            >
              {is_russian ? "RU" : "UZ"}
            </span>
          )}
        </div>
        {time_code && duration ? (
          <span className="block px-4">
            <span className="relative block h-0.5 w-full rounded-full bg-white bg-opacity-20">
              <span
                className="absolute left-0 h-0.5 rounded-full bg-orange-500"
                style={{ width: `${(time_code / duration) * 100}%` }}
              ></span>
            </span>
          </span>
        ) : null}
      </Link>
    );
  },
);

MovieCardContent.displayName = "MovieCardContent";

export default MovieCardContent;
